// Recovery from a code-split chunk that no longer exists (#11140).
//
// Production replaces the whole `dist` artifact on every release, so a tab that
// was loaded before a deploy asks for a hashed chunk that has since been
// removed. The request comes back 404 with the index.html body, which fails a
// dynamic import on both status and MIME type. Vite dispatches
// `vite:preloadError` for precisely this case, and a reload puts the user on
// the current bundle.

export const STALE_CHUNK_RELOADED_KEY = "fp_stale_chunk_reloaded";

// A second deploy while the tab is still open needs a second reload, so the
// guard is a time window rather than once per session (#12239). Five minutes
// is far longer than a reload takes, so a deploy that is genuinely broken
// still cannot loop, and far shorter than the gap between two releases.
export const STALE_CHUNK_RELOAD_WINDOW_MS = 5 * 60 * 1000;

/**
 * Reload at most once per `STALE_CHUNK_RELOAD_WINDOW_MS`. Returns whether this
 * call performed the reload.
 *
 * The guard lives in sessionStorage rather than a module-level flag because it
 * has to survive the very reload it triggers. It holds the time of the last
 * reload; anything that is not a number (the "1" older builds wrote) counts as
 * long ago.
 */
export function recoverFromStaleChunk(
  reload: () => void,
  now: number = Date.now()
): boolean {
  const lastReloadAt = Number(sessionStorage.getItem(STALE_CHUNK_RELOADED_KEY));
  if (
    Number.isFinite(lastReloadAt) &&
    now - lastReloadAt <= STALE_CHUNK_RELOAD_WINDOW_MS
  ) {
    return false;
  }
  sessionStorage.setItem(STALE_CHUNK_RELOADED_KEY, String(now));
  reload();
  return true;
}

/**
 * One listener covers every `React.lazy` call site in the app.
 *
 * Deliberately does not call `preventDefault()`: Vite's helper treats a
 * prevented event as handled and resolves the import to `undefined`, so React
 * then fails with a much more confusing "element type is invalid". Letting it
 * throw keeps the error boundaries working while the reload is already in
 * flight.
 */
export function installStaleChunkRecovery(
  reload: () => void = () => window.location.reload()
): void {
  window.addEventListener("vite:preloadError", () => {
    // Offline, the reload would swap the app for the browser's network-error
    // page and use up the guard. The contained fallback is better.
    if (!navigator.onLine) {
      return;
    }
    recoverFromStaleChunk(reload);
  });
}
